// ============================================================================
// 地名検索（ジオコーディング）/ Nominatim (OpenStreetMap)
// ----------------------------------------------------------------------------
// APIキー不要・CORS 許可。利用ポリシー上、対話的入力はデバウンスして
// 過剰リクエストを避けること（app 側で実施）。
// 出典: /Users/ntuf/Workspace/ShadowStep の src/lib/geocode.ts をそのまま移植。
// ============================================================================

export interface GeoResult {
  name: string;
  lng: number;
  lat: number;
  /** [west, south, east, north] または null。 */
  bbox: [number, number, number, number] | null;
  /** 種別ラベル（表示用の絵文字選択に使用）。 */
  category: string;
  type: string;
}

const ENDPOINT = "https://nominatim.openstreetmap.org/search";

export async function searchPlaces(
  query: string,
  signal?: AbortSignal,
  /** 現在表示中の範囲 [west, south, east, north]。近傍結果を優先する。 */
  viewbox?: [number, number, number, number]
): Promise<GeoResult[]> {
  const q = query.trim();
  if (q.length < 2) return [];

  let url =
    `${ENDPOINT}?q=${encodeURIComponent(q)}` +
    `&format=jsonv2&limit=6&addressdetails=0&accept-language=ja&countrycodes=jp`;
  if (viewbox) {
    // viewbox=lon1,lat1,lon2,lat2（bounded=0 なので範囲外も許容しつつ近傍を優先）
    url += `&viewbox=${viewbox[0]},${viewbox[3]},${viewbox[2]},${viewbox[1]}&bounded=0`;
  }

  const res = await fetch(url, {
    signal,
    headers: { Accept: "application/json" },
  });
  if (!res.ok) throw new Error(`geocode ${res.status}`);
  const data = (await res.json()) as any[];

  return data.map((d) => {
    // boundingbox: [south, north, west, east]（文字列）
    let bbox: [number, number, number, number] | null = null;
    if (Array.isArray(d.boundingbox) && d.boundingbox.length === 4) {
      const s = parseFloat(d.boundingbox[0]);
      const n = parseFloat(d.boundingbox[1]);
      const w = parseFloat(d.boundingbox[2]);
      const e = parseFloat(d.boundingbox[3]);
      if (![s, n, w, e].some(isNaN)) bbox = [w, s, e, n];
    }
    return {
      name: d.display_name as string,
      lng: parseFloat(d.lon),
      lat: parseFloat(d.lat),
      bbox,
      category: d.category ?? d.class ?? "",
      type: d.type ?? "",
    };
  });
}

/** 種別に応じた絵文字アイコン。 */
export function placeIcon(r: GeoResult): string {
  const c = r.category;
  const t = r.type;
  if (c === "railway" || t === "station") return "🚉";
  if (c === "aeroway") return "✈️";
  if (c === "amenity") return "📍";
  if (c === "building") return "🏢";
  if (c === "shop") return "🛍️";
  if (c === "tourism") return "🗼";
  if (c === "natural" || c === "leisure") return "🌳";
  if (c === "place") {
    if (["city", "town", "village", "suburb"].includes(t)) return "🏙️";
    return "📌";
  }
  if (c === "highway") return "🛣️";
  return "📍";
}

/** 表示名を「主要部分・地域」に短く整形。 */
export function formatName(name: string): { primary: string; secondary: string } {
  const parts = name.split(",").map((s) => s.trim());
  const primary = parts[0] ?? name;
  const secondary = parts.slice(1, 4).join("・");
  return { primary, secondary };
}
